// which bumper plays when (config/clock.json `voice`, decided Oct 25: "not a chatterbox"). Her voice opens the
// features, a station ID plays at :00 and :30 with the bell, a daypart line takes the :00 when a daypart begins (with
// the foghorn for the night sign-on), a crab-day or seasonal line takes the :30 on its days, interrupts get their own
// lines and stings. Outros are a silent plate wipe. Bell rule (Oct 27, second pass): the bell rings only at the :00
// and :30 station breaks (and at night only the :00); feature intros are her voice alone (`bells.featureIntros` in
// clock.json).
import { daypart } from './music.mjs';
import { data } from './snapshot.mjs';
import crabDays from './crab.mjs';
import { dayKey, isToday } from './format.mjs';

const { clock, seasons } = window.RHL;
const { voice } = clock;
const CRAB_DAY_SCORE = 8;

// rotation through a list, per list
const turns = new Map();
const nextOf = (key, list) => {
	const i = turns.get(key) ?? 0;
	turns.set(key, (i + 1) % list.length);
	return list[i];
};

const FEATURE_TITLES = {
	'market-closeup': ['Markets', 'on the 3s'],
	'around-town': ['Around', 'Town'],
	'news-desk': ['News', 'Desk'],
	'views-around-town': ['Views Around', 'Town'],
	'views-block': ['Views', 'Around the Bay'],
	'live-look': ['Live', 'Look'],
};

// a feature intro is her line with no bell unless `bells.featureIntros` turns it back on (it rang Oct 27 until the
// afternoon); one without a line drops the plate in silence. No intro bell at night either way (Phase D)
const nightBells = clock.night?.bells ?? {};
const introBells = clock.bells?.featureIntros === true;
const featureIntro = (name, upNext) => {
	const line = voice.featureIntros[name] ? nextOf(name, voice.featureIntros[name]) : null;
	const quiet = daypart(new Date()).name === 'night' && nightBells.featureIntros === false;
	return {
		title: FEATURE_TITLES[name] ?? [name, ''], line, sting: line && introBells && !quiet ? 'bell-2' : null, upNext,
	};
};

// back to the loop: a plate wipe, no sound
// back to the loop after a feature: "up next" holds about 2.5 s and the plate fades off slowly (Oct 26, Jared: the
// throw back felt rushed; it used to show "up next" for under a second and cut)
const outro = (upNext) => ({
	title: ['Russian Hill', 'Local'], line: null, sting: null, upNext, hold: 2500, fade: 700,
});

// fog tonight: the forecasters mention it in the discussion
const foggy = () => (data('fog')?.lines?.length ?? 0) > 0;

// a crab day beats a seasonal line
const specialFor = (now) => {
	const [next] = crabDays({ tides: data('tides'), swell: data('swell'), wind: data('wind') }, now);
	if (next && isToday(next.high.t, now) && next.score >= CRAB_DAY_SCORE) return '4C';
	const today = dayKey(now);
	return seasons.lines.find((s) => today >= s.from && today <= s.to)?.line ?? null;
};

// the :00 and :30 slots: a daypart line when one begins this hour, a special at :30, else the next station ID
const slot = (minute, now = new Date()) => {
	if (!voice.stationId.minutes.includes(minute)) return null;
	const { name: part, weekend } = daypart(now);
	const hour = Number(new Intl.DateTimeFormat('en-US', { timeZone: clock.timeZone, hour: 'numeric', hourCycle: 'h23' }).format(now));
	const begins = clock.dayparts.find((d) => d.start === hour)?.name;
	if (minute === 0 && begins === part) {
		if (part === 'night') {
			return {
				title: ['Good', 'Night'], line: foggy() ? voice.dayparts.nightFog : voice.dayparts.night, sting: 'foghorn', kind: 'sign-on',
			};
		}
		const line = part === 'morning' && weekend ? voice.dayparts.weekendMorning : voice.dayparts[part];
		if (line) return { title: ['Russian Hill', 'Local'], line, sting: 'bell-2', kind: 'daypart' };
	}
	if (minute === voice.specials.replaces) {
		const special = specialFor(now);
		if (special) return { title: ['Russian Hill', 'Local'], line: special, sting: 'bell-2', kind: 'special' };
	}
	// at night only the IDs in night.bells.stationIdMinutes ring (Phase D: few bells)
	const ring = part !== 'night' || !nightBells.stationIdMinutes || nightBells.stationIdMinutes.includes(minute);
	return {
		title: ['Russian Hill', 'Local'], line: nextOf('station', voice.stationId.lines), sting: ring ? 'bell-2' : null, kind: 'station-id',
	};
};

// interrupts: the weather gets the foghorn, the rest the bell; the close line ends them all
const interruptIntro = (kind) => ({
	weather: { title: ['Weather', 'Statement'], line: voice.interrupts.weather, sting: 'foghorn' },
	quake: { title: ['Earthquake', 'Information'], line: voice.interrupts.quake, sting: 'bell-2' },
	halt: { title: ['Trading', 'Halt'], line: voice.interrupts.halt, sting: 'bell-2' },
}[kind] ?? { title: ['Russian Hill', 'Local'], line: null, sting: 'bell-2' });
const interruptClose = (upNext) => ({
	title: ['Russian Hill', 'Local'], line: voice.interrupts.close, sting: null, upNext,
});

export {
	featureIntro,
	outro,
	slot,
	interruptIntro,
	interruptClose,
	FEATURE_TITLES,
};
